import { Icon } from "@/components/Icon/Icon";
import type { IconName } from "@/utils/types/ui";
import type { SubstanceRow } from "../_model";
import { ReadinessFlag } from "./ReadinessFlag";

interface CompositionCellProps {
  substance: SubstanceRow;
  onView?: (s: SubstanceRow) => void;
  dense?: boolean;
}

export function CompositionCell({ substance, onView, dense }: CompositionCellProps) {
  const s = substance;

  // A single compound is fully defined by its identifiers — no composition needed.
  if (s.type !== "mixture") {
    return <span style={{ fontSize: 12, color: "var(--ink-mute)" }}>—</span>;
  }

  // A mixture's composition can be supplied via a chromatogram OR a formulation
  // (both editable in the drawer). Until it's READY, surface the readiness status
  // here on a single line — no inline "add chromatogram" affordance. Once READY,
  // show the registered chromatogram / formulation counts (click to view in the drawer).
  if (s.status !== "READY") {
    return <ReadinessFlag status={s.status} />;
  }

  const hasChrom = s.chrom > 0;
  const hasForm = !!s.formula;

  const Item = ({ icon, count }: { icon: IconName; count: number }) => (
    <span className="hstack" style={{ gap: 5, alignItems: "center" }}>
      <Icon name={icon} size={13} />
      <span className="mono" style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink)", letterSpacing: 0 }}>{count}</span>
    </span>
  );

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onView?.(s); }}
      className="sl-view-btn"
      title="View registered composition data"
      style={{
        display: "inline-flex", alignItems: "center", gap: 9,
        padding: dense ? "2px 9px" : "4px 10px", borderRadius: 6,
        background: "var(--panel)", border: "1px solid var(--line)", color: "var(--ink-2)", cursor: "pointer",
      }}
    >
      {hasChrom && <Item icon="chromatogram" count={s.chrom} />}
      {hasChrom && hasForm && <span style={{ color: "var(--line-strong)" }}>·</span>}
      {hasForm && <Item icon="beaker" count={1} />}
    </button>
  );
}
